<template>
  <header
    class="sticky top-0 z-30 flex min-h-[68px] flex-none items-center gap-2.5 border-b bg-background/80 px-[18px] py-2.5 backdrop-blur-md md:h-[68px] md:gap-3.5 md:px-[30px] md:py-0 [backdrop-filter:saturate(140%)_blur(14px)]"
  >
    <MobileSidebar />

    <NavbarSearch />

    <div class="flex-1"></div>

    <!-- Leads to /plans (current plan, comparison, upgrade). Tighter on phones so it fits
         beside the menu, language, theme and avatar controls -->
    <NuxtLink
      v-if="planPill"
      to="/plans"
      :aria-label="`${planPill}: ${t('common.chooseYourPlan')}`"
      :title="t('common.chooseYourPlan')"
      class="inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[12.5px] font-semibold text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:gap-2 sm:px-[13px] sm:py-[7px] sm:text-[13px] [@media(pointer:coarse)]:min-h-11"
    >
      <span class="h-[7px] w-[7px] flex-none rounded-full" :class="isPaidPlan ? 'bg-primary' : 'bg-subtle-foreground'"></span>
      <span class="truncate">{{ planPill }}</span>
    </NuxtLink>

    <div class="flex items-center gap-1.5 md:gap-2">
      <NuxtLink
        v-if="user"
        to="/logs"
        class="hidden text-sm text-muted-foreground italic px-1 sm:inline"
      >
        v{{ version }}
      </NuxtLink>
      <span v-else class="hidden text-sm text-muted-foreground italic px-1 sm:inline">v{{ version }}</span>

      <LanguageToggle />
      <ThemeToggle />

      <DropdownMenu v-if="user">
        <DropdownMenuTrigger
          id="user-menu-button"
          class="grid h-9 w-9 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 place-items-center overflow-hidden rounded-full bg-primary text-sm font-bold text-primary-foreground"
          :aria-label="$t('common.navAccount')"
        >
          <img
            v-if="user?.user_metadata?.avatar_url"
            :src="user.user_metadata.avatar_url"
            alt=""
            class="w-full h-full rounded-full object-cover"
          />
          <template v-else>{{ user?.email?.charAt(0).toUpperCase() }}</template>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-72">
          <DropdownMenuLabel>{{ $t("common.accountDetails") }}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem as-child class="cursor-pointer p-2 ps-3">
            <NuxtLink to="/settings" class="flex w-full">
              <Avatar>
                <AvatarImage v-if="user?.user_metadata.avatar_url" :src="user?.user_metadata.avatar_url" />
                <AvatarFallback>
                  {{ user?.email?.charAt(0).toUpperCase() }}
                  {{ user?.email?.charAt(1).toUpperCase() }}
                </AvatarFallback>
              </Avatar>
              <div>
                <div class="font-bold">{{ user?.user_metadata.full_name }}</div>
                <div class="text-sm">{{ user?.email }}</div>
              </div>
            </NuxtLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem @click="logout" class="cursor-pointer">
            <Icon class="me-2 h-5 w-5" name="lucide:log-out" />
            <span class="ms-2">{{ $t("common.logout") }}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <!-- Link styled as a button (local Button has no as-child) -->
      <NuxtLink v-else to="/auth" :class="buttonVariants({ variant: 'default', size: 'sm' })">
        <Icon name="lucide:log-in" class="h-4 w-4 me-2" aria-hidden="true" />
        {{ $t("authModal.loginButton") }}
      </NuxtLink>
    </div>
  </header>
</template>

<script setup lang="ts">
import { useUser } from "@/composables/useUser";
import { buttonVariants } from "@/components/ui/button";

const user = useSupabaseUser();
const { user: profile } = useUser();
const supabaseClient = useSupabaseClient();
const router = useRouter();
const { t } = useI18n();

// Plan pill mirrors the dashboard label so a trial / pending-payment user is
// never mislabelled "Premium" in the topbar. Hidden while signed out or the
// profile is still loading.
const planPill = computed(() => {
  if (!user.value) return "";
  switch (profile.value?.planState) {
    case "free":
      return t("common.free");
    case "premium":
      return t("common.premium");
    case "lifetime":
      return t("common.lifetime");
    case "trial":
      return t("common.trial");
    case "processing":
      return t("common.processing");
    default:
      return "";
  }
});
const isPaidPlan = computed(() => profile.value?.planState !== "free");

const logout = async () => {
  await supabaseClient.auth.signOut();
  const { clearUser } = useUser();
  clearUser();
  router.push("/auth");
};
const config = useRuntimeConfig();
const version = ref(config.public.version || "0.0.1");
</script>

<style scoped></style>
